import React, { useState } from 'react';
import { Card, Typography, Input, Button, Space, message, Spin, Row, Col } from 'antd';
import { SendOutlined, CopyOutlined } from '@ant-design/icons';
import { streamingChat } from 'api/ai';
import type { TStreamingChatRes } from 'types/ai';
import { getUrlParam } from 'utils/common';
import s from './s.module.less';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface IGeneratedRule {
    id: string;
    question: {
        id: string;
        title: string;
        type: string;
    };
    answer: {
        value: string | string[] | number;
        id: string | string[] | null;
    };
    result: {
        type: 'not_suitable' | 'video_visit' | 'text_visit' | 'continue';
    };
    triggerTiming: 'immediate' | 'after_last_question';
}

const AIRuleGenerator: React.FC = () => {
    const [prompt, setPrompt] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [generatedRules, setGeneratedRules] = useState<IGeneratedRule[]>([]);
    const [jsonOutput, setJsonOutput] = useState<string>('');

    const assessmentUuid = getUrlParam('uuid');

    const generateRules = async () => {
        if (!prompt.trim()) {
            message.warning('Please enter a prompt to generate rules');
            return;
        }

        if (!assessmentUuid) {
            message.error('Assessment UUID not found in URL');
            return;
        }

        setLoading(true);
        try {
            const chatParams: TStreamingChatRes = {
                inputs: {},
                query: `Generate decision tree rules based on this prompt: "${prompt}". 
                
                Please generate rules in the following JSON format:
                {
                    "assessmentUuid": "${assessmentUuid}",
                    "rules": [
                        {
                            "id": "rule_1",
                            "question": {
                                "id": "question_id",
                                "title": "Question text",
                                "type": "questionFrom"
                            },
                            "answer": {
                                "value": "answer_value",
                                "id": "answer_id"
                            },
                            "result": {
                                "type": "not_suitable" | "video_visit" | "text_visit" | "continue"
                            },
                            "triggerTiming": "immediate" | "after_last_question"
                        }
                    ]
                }
                
                Generate 3-5 relevant rules based on the prompt. Use appropriate result types and trigger timings.`,
                user: 'admin',
                response_mode: 'streaming',
                do_evaluation: false,
            };

            const response = await streamingChat(chatParams);

            // Process the streaming response
            let fullResponse = '';
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (reader) {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = decoder.decode(value);
                    const lines = chunk.split('\n');

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(line.slice(6));
                                if (data.answer) {
                                    fullResponse += data.answer;
                                }
                            } catch (e) {
                                // Ignore parsing errors for non-JSON lines
                            }
                        }
                    }
                }
            }

            // Try to extract JSON from the response
            const jsonMatch = fullResponse.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
                try {
                    const parsedData = JSON.parse(jsonMatch[0]);
                    if (parsedData.rules && Array.isArray(parsedData.rules)) {
                        setGeneratedRules(parsedData.rules);
                        setJsonOutput(JSON.stringify(parsedData, null, 2));
                        message.success('Rules generated successfully!');
                    } else {
                        throw new Error('Invalid JSON structure');
                    }
                } catch (e) {
                    message.error('Failed to parse generated rules. Please try again.');
                    console.error('JSON parsing error:', e);
                }
            } else {
                message.error('No valid JSON found in the response. Please try again.');
            }
        } catch (error) {
            message.error('Failed to generate rules. Please try again.');
            console.error('Error generating rules:', error);
        } finally {
            setLoading(false);
        }
    };

    const copyJson = () => {
        navigator.clipboard.writeText(jsonOutput);
        message.success('JSON copied to clipboard!');
    };

    return (
        <div className={s.aiRuleGenerator}>
            <div>
                <Title level={4}>Enter your prompt:</Title>
                <TextArea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="to do"
                    rows={4}
                    className={s.promptInput}
                />
                <Button
                    type="primary"
                    onClick={generateRules}
                    loading={loading}
                    disabled={!prompt.trim()}
                    className={s.generateButton}
                >
                    Generate Rules
                </Button>
            </div>

            {loading && (
                <div className={s.loadingContainer}>
                    <Spin size="large" />
                    <Paragraph>Generating rules with AI...</Paragraph>
                </div>
            )}

            {generatedRules.length > 0 && (
                <div>
                    <Title level={4}>Generated Rules:</Title>
                    <div className={s.rulesList}>
                        {generatedRules.map((rule, index) => (
                            <Card key={rule.id} size="small" className={s.ruleCard}>
                                <Row gutter={16}>
                                    <Col span={12}>
                                        <div className={s.ruleField}>
                                            <strong>Question:</strong> {rule.question.title}
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <div className={s.ruleField}>
                                            <strong>Answer:</strong> {Array.isArray(rule.answer.value) ? rule.answer.value.join(', ') : rule.answer.value}
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <div className={s.ruleField}>
                                            <strong>Result:</strong> {rule.result.type}
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {jsonOutput && (
                <div>
                    <div className={s.jsonHeader}>
                        <Title level={4}>JSON Output:</Title>
                        <Button
                            icon={<CopyOutlined />}
                            onClick={copyJson}
                            size="small"
                        >
                            Copy JSON
                        </Button>
                    </div>
                    <pre className={s.jsonCode}>
                        {jsonOutput}
                    </pre>
                </div>
            )}
        </div>
    );
};

export default AIRuleGenerator;
